/* eslint-disable no-nested-ternary */
import { ModifySlot, SlotItem } from './types';

const INTERVAL_MINUTES = 15;
const MINUTES_PER_DAY = 24 * 60;
const LAST_SLOT_MINUTE = MINUTES_PER_DAY - INTERVAL_MINUTES;

export type TimeFieldRole = 'start' | 'end';

export type TimeBoundaryContext = {
  fieldRole: TimeFieldRole;
  slotIndex: number;
  daySlots: ModifySlot[];
};

const pad2 = (n: number): string => n.toString().padStart(2, '0');

/** Normalize hour/minute for storage and API payload (always two digits). */
export const normalizeTimePart = (
  value: number | string | undefined,
): string => {
  if (value === '' || value === undefined) {
    return '';
  }
  const num = parseInt(`${value}`, 10);
  if (Number.isNaN(num)) {
    return '';
  }
  return pad2(num);
};

export const formatTimeLabel = (hour: number, minute: number): string =>
  `${pad2(hour)}:${pad2(minute)}`;

export const slotItemToMinutes = (item: SlotItem): number | null => {
  if (
    item.hour === '' ||
    item.minute === '' ||
    item.hour === undefined ||
    item.minute === undefined
  ) {
    return null;
  }
  const hour = parseInt(`${item.hour}`, 10);
  const minute = parseInt(`${item.minute}`, 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  return hour * 60 + minute;
};
export const isSlotTimeFilled = (item: SlotItem): boolean =>
  slotItemToMinutes(item) !== null;

export const minutesToSlotItem = (totalMinutes: number): SlotItem => {
  const clamped = Math.max(0, Math.min(totalMinutes, MINUTES_PER_DAY - 1));
  return {
    hour: Math.floor(clamped / 60),
    minute: clamped % 60,
  };
};

export const formatDisplayValue = (
  hour: SlotItem['hour'],
  minute: SlotItem['minute'],
): string => {
  const total = slotItemToMinutes({ hour, minute });
  if (total === null) {
    return '';
  }
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${pad2(h)} : ${pad2(m)}`;
};

const MAX_TIME_INPUT_DIGITS = 4;

/** Keep at most 4 numeric digits from raw input */
export const extractTimeDigits = (raw: string): string =>
  raw.replace(/\D/g, '').slice(0, MAX_TIME_INPUT_DIGITS);

/** Time segment types for segmented input */
export type TimeSegment = 'hour' | 'minute';

/** Get which segment (hour/minute) contains the cursor position */
export const getTimeSegmentFromPosition = (
  value: string,
  position: number,
): TimeSegment =>
  // In "HH : MM" format, positions 0-2 are hours, 5-7 are minutes
  position <= 2 ? 'hour' : 'minute';

/** Select entire time segment (hour or minute) */
export const selectTimeSegment = (
  input: HTMLInputElement,
  segment: TimeSegment,
): void => {
  if (segment === 'hour') {
    // Select "HH" part (positions 0-2)
    input.setSelectionRange(0, 2);
  } else {
    // Select "MM" part (positions 5-7 in "HH : MM")
    const { value } = input;
    const colonIndex = value.indexOf(':');
    if (colonIndex !== -1) {
      const minuteStart = colonIndex + 2; // Skip " : "
      input.setSelectionRange(minuteStart, minuteStart + 2);
    }
  }
};

/** Parse current hour/minute values from formatted input */
export const parseTimeSegments = (
  value: string,
): { hour: number | null; minute: number | null } => {
  const match = value.match(/^(\d{1,2})\s*:\s*(\d{1,2})$/);
  if (!match) {
    return { hour: null, minute: null };
  }
  return {
    hour: parseInt(match[1], 10),
    minute: parseInt(match[2], 10),
  };
};

/** Format time segments into display string */
export const formatTimeSegments = (
  hour: number | null,
  minute: number | null,
): string => {
  if (hour === null || minute === null) {
    return '';
  }
  return `${pad2(hour)} : ${pad2(minute)}`;
};

/** Increment/decrement a time segment with bounds checking */
export const adjustTimeSegment = (
  segment: TimeSegment,
  value: number,
  delta: number,
): number => {
  const newValue = value + delta;

  if (segment === 'hour') {
    if (newValue < 0) return 23;
    if (newValue > 23) return 0;
    return newValue;
  }
  if (newValue < 0) return 59;
  if (newValue > 59) return 0;
  return newValue;
};

/** Format digits as "hh : mm" while typing; auto-inserts " : " after 2 digits */
export const formatTimeInputWhileTyping = (digits: string): string => {
  if (!digits) {
    return '';
  }
  if (digits.length < 2) {
    return digits;
  }
  const hours = digits.slice(0, 2);
  const minutes = digits.slice(2);
  return minutes ? `${hours} : ${minutes}` : `${hours} : `;
};

/** Convert exactly 4 typed digits into hour/minute (may be out of range). */
export const digitsToSlotItem = (digits: string): SlotItem | null => {
  if (digits.length !== 4) {
    return null;
  }
  return {
    hour: parseInt(digits.slice(0, 2), 10),
    minute: parseInt(digits.slice(2, 4), 10),
  };
};

export const isValidTimeValues = (hour: number, minute: number): boolean =>
  !Number.isNaN(hour) &&
  !Number.isNaN(minute) &&
  hour >= 0 &&
  hour <= 23 &&
  minute >= 0 &&
  minute <= 59;

/** Parse "9:07", "09:07", "09 : 07", partial while typing */
export const parseTimeInput = (
  raw: string,
): { hour: number; minute: number } | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = trimmed.replace(/\s+/g, '');
  const match = normalized.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) {
    return null;
  }
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (!isValidTimeValues(hour, minute)) {
    return null;
  }
  return { hour, minute };
};

export const generateAll15MinuteLabels = (): string[] => {
  const labels: string[] = [];
  for (let m = 0; m < MINUTES_PER_DAY; m += INTERVAL_MINUTES) {
    labels.push(formatTimeLabel(Math.floor(m / 60), m % 60));
  }
  return labels;
};

const ALL_15_MIN_LABELS = generateAll15MinuteLabels();

export const labelToMinutes = (label: string): number => {
  const [h, m] = label.split(':').map((v) => parseInt(v, 10));
  return h * 60 + m;
};

export const getBoundaryMinutes = (
  context: TimeBoundaryContext,
): { min: number; max: number; scrollTo: number } => {
  const { fieldRole, slotIndex, daySlots } = context;
  const slot = daySlots[slotIndex];
  const prevSlot = slotIndex > 0 ? daySlots[slotIndex - 1] : undefined;
  const nextSlot =
    slotIndex < daySlots.length - 1 ? daySlots[slotIndex + 1] : undefined;

  const startMin = slotItemToMinutes(slot.start);
  const endMin = slotItemToMinutes(slot.end);
  const prevEndMin =
    prevSlot && isSlotTimeFilled(prevSlot.end)
      ? slotItemToMinutes(prevSlot.end)
      : null;
  const nextSlotIsComplete =
    !!nextSlot &&
    isSlotTimeFilled(nextSlot.start) &&
    isSlotTimeFilled(nextSlot.end);
  const nextStartMin =
    nextSlotIsComplete && nextSlot ? slotItemToMinutes(nextSlot.start) : null;

  let min = 0;
  let max = LAST_SLOT_MINUTE;
  let scrollTo = 0;

  if (fieldRole === 'start') {
    if (slotIndex === 0) {
      min = 0;
      max = endMin !== null ? endMin : LAST_SLOT_MINUTE;
      scrollTo =
        startMin !== null
          ? startMin
          : endMin !== null
          ? Math.max(0, endMin - 60)
          : 9 * 60;
    } else {
      min = prevEndMin !== null ? prevEndMin : 0;
      max = endMin !== null ? endMin : LAST_SLOT_MINUTE;
      scrollTo =
        startMin !== null ? startMin : prevEndMin !== null ? prevEndMin : min;
    }
  } else {
    min = startMin !== null ? Math.min(startMin + 1, LAST_SLOT_MINUTE) : 0;
    max =
      nextStartMin !== null ? Math.max(min, nextStartMin) : LAST_SLOT_MINUTE;
    scrollTo =
      endMin !== null
        ? endMin
        : startMin !== null
        ? Math.min(startMin + INTERVAL_MINUTES, max)
        : min;
  }

  if (min > max) {
    min = max;
  }

  scrollTo = Math.max(min, Math.min(scrollTo, max));

  return { min, max, scrollTo };
};

export const getDropdownOptions = (
  context: TimeBoundaryContext,
): { label: string; minutes: number }[] => {
  const { min, max } = getBoundaryMinutes(context);
  const { fieldRole, slotIndex, daySlots } = context;

  let options = ALL_15_MIN_LABELS.map((label) => ({
    label,
    minutes: labelToMinutes(label),
  })).filter(({ minutes }) => minutes >= min && minutes <= max);

  if (fieldRole === 'end') {
    const slot = daySlots[slotIndex];
    const startMin = slotItemToMinutes(slot.start);
    if (startMin !== null) {
      options = options.filter(({ minutes }) => minutes > startMin);
    }
  }

  return options;
};
